/**
 * Shared model for the redesigned mobile Products screen (Trioty Products
 * Mobile): the All/In-stock/Low/Out tabs, the Category/None grouping, the sort
 * comparators, and the row-detail field list.
 *
 * Sibling of orders-mobile-model.ts — same shape, products semantics. All of it
 * is derived from our real ProductView / ProductCardStatus so tabs, grouping and
 * sorting stay backed by the same logic the desktop grid/table use.
 */

import type { ProductListResponse } from "@/lib/api/types";
import { keys } from "@/lib/i18n/portal";
import {
  deriveProductStatus,
  getPurchasableStockTotal,
  hasUnlimitedStock,
  type ProductCardStatus,
} from "@/modules/products/lib/cardAttention";
import type { ProductsSort } from "@/modules/products/components/ProductsHeroBar";

export type ProductItem = ProductListResponse["items"][number];

export type ProductsTab = "all" | "active" | "low_stock" | "out_of_stock";
export type ProductsGroupBy = "category" | "none";
export type { ProductsSort };
export type RowProp = "category" | "stock";

/** Filter selections for the mobile Products filter sheet. */
export type ProductsFilterState = {
  /** Selected category ids — fed into useProductsQuery `categoryIds`. */
  category: string[];
};

export const EMPTY_PRODUCTS_FILTERS: ProductsFilterState = { category: [] };

// `all` first — it's the default landing tab; the rest mirror the desktop
// status pills (hidden intentionally omitted; hidden products live under All).
export const PRODUCTS_TABS: ProductsTab[] = [
  "all",
  "active",
  "low_stock",
  "out_of_stock",
];

/** i18n label key for each tab. */
export const TAB_LABEL: Record<ProductsTab, string> = {
  all: keys.common.all,
  active: keys.products.mTabInStock,
  low_stock: keys.products.mTabLow,
  out_of_stock: keys.products.mTabOut,
};

/** Tab membership: All shows everything (incl. hidden); the status tabs show
 *  only their matching derived status. */
export function inTab(status: ProductCardStatus, tab: ProductsTab): boolean {
  if (tab === "all") return true;
  return status === tab;
}

export const GROUP_LABEL: Record<ProductsGroupBy, string> = {
  category: keys.products.category,
  none: keys.orders.groupByNone,
};

export const SORT_LABEL: Record<ProductsSort, string> = {
  recent: keys.products.sortRecentlyAdded,
  sales: keys.products.sortBestSelling,
  "price-desc": keys.products.sortPriceHighToLow,
  "price-asc": keys.products.sortPriceLowToHigh,
  "stock-asc": keys.products.sortStockLowToHigh,
  "stock-desc": keys.products.sortStockHighToLow,
};

/** Default-variant price (discounted when present) — mirrors ProductsPage's
 *  `sortedItems` priceOf so mobile and desktop sort identically. */
function priceOf(p: ProductItem): number {
  const v =
    p.variants.find((x) => x.id === p.defaultVariantId) ??
    p.variants.find((x) => x.isDefault) ??
    p.variants[0];
  return v ? (v.discountedPrice ?? v.price) : 0;
}

/** Client-side comparators over the loaded page. `recent`/`sales` keep the
 *  backend order (stable sort) — no local re-ordering. */
export function sortComparator(
  sort: ProductsSort,
): (a: ProductItem, b: ProductItem) => number {
  switch (sort) {
    case "price-asc":
      return (a, b) => priceOf(a) - priceOf(b);
    case "price-desc":
      return (a, b) => priceOf(b) - priceOf(a);
    case "stock-asc":
      return (a, b) => getPurchasableStockTotal(a) - getPurchasableStockTotal(b);
    case "stock-desc":
      return (a, b) => getPurchasableStockTotal(b) - getPurchasableStockTotal(a);
    case "sales":
    case "recent":
    default:
      return () => 0;
  }
}

// Re-exported so the view/sheets import the derivation from one module.
export { deriveProductStatus, getPurchasableStockTotal, hasUnlimitedStock };
export type { ProductCardStatus };
